/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

/**
 * MeasurementService.js
 *
 * Extracts body measurements from segmentation masks using rotation method
 * From thesis utils.ts bodyMeasurement()
 *
 * Pipeline Step 2: Vermessung (Measurement)
 * Input: Segmentation mask (tf.Tensor2D) + bounding box
 * Output: Pixel measurements [bodyLength, shoulderHeight, rumpHeight, bodyHeight]
 */

import * as tf from '@tensorflow/tfjs'

/**
 * Service for extracting measurements from segmentation masks
 */
export class MeasurementService {
  constructor() {
    // Body region ratios (from thesis utils.ts)
    this.BODY_REGIONS = {
      head: 0.2,
      shoulder: 0.4,
      rump: 0.3,
      tail: 0.1,
    }

    // Rotation angles for length measurement (from thesis)
    this.ROTATION_ANGLES = {
      left: -12.5,
      right: 12.5,
    }

    this.MIDDLE_HEIGHT_RATIO = 0.5 // Measure from middle of body
  }

  /**
   * Extract body measurements from segmentation mask
   * Measures body length, height, and rump height in pixels
   *
   * @param {tf.Tensor2D} mask - Binary segmentation mask tensor
   * @param {Object} box - Bounding box from segmentation
   * @param {string} direction - "left" or "right" (goat orientation)
   * @param {HTMLCanvasElement} canvas - Optional debug canvas
   * @returns {Object} Pixel measurements
   */
  async extractMeasurements(mask, box, direction = 'left', canvas = null) {
    performance.mark('start_measurement')

    console.groupCollapsed('[Measurement] Extraction')
    console.info('Mask shape:', mask.shape)
    console.info('Bounding Box:', box)
    console.info('Direction:', direction)

    try {
      // Crop mask to bounding box
      const detection = mask.slice(
        [box.topY(), box.topX()],
        [box.height(), box.width()]
      )

      const height = box.height()
      const width = detection.shape[1]

      // Find filled columns
      const range = tf.range(1, width + 1, 1)
      const filledColumns = detection.sum(0).toBool().toInt()
      const xStart = filledColumns.mul(range.reverse()).argMax()
      const xEnd = filledColumns.mul(range).argMax()

      // Crop to actual goat width
      const xStartVal = (await xStart.data())[0]
      const xEndVal = (await xEnd.data())[0]
      const croppedDetection = detection.slice(
        [0, xStartVal],
        [height, xEndVal - xStartVal]
      )

      const actualWidth = croppedDetection.shape[1]
      const x = xStartVal + box.topX()

      // Calculate lastIndices (for each X column, find lowest Y where mask > 0)
      const colRange = tf.range(1, height + 1, 1)
      const lastIndices = croppedDetection
        .mul(colRange.expandDims(-1))
        .argMax(0)

      // Calculate body regions based on direction
      const regions = this.calculateBodyRegions(actualWidth, direction)

      console.info('Body regions:', regions)
      console.info('Last indices shape:', lastIndices.shape)

      // Find lowest indices in shoulder and rump regions
      const shoulderSide = lastIndices.slice(
        [regions.shoulderStart],
        [regions.shoulderWidth]
      )
      const lowestShoulderIndex = shoulderSide.argMax()
      const lowestShoulderIndexVal = (await lowestShoulderIndex.data())[0]

      this.drawVerticalLine(
        canvas,
        lowestShoulderIndexVal + x + regions.shoulderStart,
        'green'
      )

      const rumpSide = lastIndices.slice(
        [regions.rumpStart],
        [regions.rumpWidth]
      )
      const lowestRumpIndex = rumpSide.argMax()
      const lowestRumpIndexVal = (await lowestRumpIndex.data())[0]

      // Find hill (peak of rump)
      let hill
      if (direction === 'left') {
        hill = this.firstHill(
          croppedDetection
            .mul(colRange.reverse().expandDims(1))
            .argMax(0)
            .slice(regions.rumpStart, regions.rumpWidth)
        )
      } else {
        hill = tf
          .scalar(regions.rumpWidth)
          .sub(
            this.firstHill(
              croppedDetection
                .mul(colRange.reverse().expandDims(1))
                .argMax(0)
                .slice(regions.rumpStart, regions.rumpWidth)
                .reverse()
            )
          )
      }

      const hillVal = (await hill.data())[0]
      this.drawVerticalLine(canvas, hillVal + x + regions.rumpStart, 'grey')
      this.drawVerticalLine(
        canvas,
        lowestRumpIndexVal + x + regions.rumpStart,
        'red'
      )

      // Calculate middle position
      const newMiddle = tf
        .scalar(lowestShoulderIndexVal + regions.shoulderStart, 'int32')
        .add(tf.scalar(lowestRumpIndexVal + regions.rumpStart, 'int32'))
        .div(tf.scalar(2, 'int32'))

      const newMiddleVal = (await newMiddle.data())[0]
      this.drawVerticalLine(canvas, newMiddleVal + x, 'purple')

      // Find middle start and end (body height)
      const middleColumn = croppedDetection.gather(newMiddle, 1).squeeze()
      const middleStart = middleColumn.mul(colRange.reverse()).argMax()
      const middleEnd = middleColumn.mul(colRange).argMax()
      const middleLength = middleEnd.sub(middleStart)

      const middleStartVal = (await middleStart.data())[0]
      const middleEndVal = (await middleEnd.data())[0]

      this.draw(
        canvas,
        newMiddleVal,
        middleStartVal,
        newMiddleVal,
        middleEndVal,
        'yellow',
        x,
        box.topY()
      )

      const bodyHeight = middleEndVal - middleStartVal

      // Calculate body length at middle height
      // The body length is simply the width of the detected goat at the center line
      const bodyLengthIndex = middleLength
        .mul(tf.scalar(0.5))
        .add(middleStart)
        .cast('int32')
      const bodyLengthIndexVal = (await bodyLengthIndex.data())[0]

      // Get the horizontal slice at body center
      const bodyLengthLine = croppedDetection
        .gather(bodyLengthIndex, 0)
        .squeeze()

      // Find leftmost and rightmost pixels (not just longest consecutive run)
      const bodyRange = tf.range(1, actualWidth + 1)
      const bodyLengthStartTensor = bodyLengthLine
        .mul(bodyRange.reverse())
        .argMax()
      const bodyLengthEndTensor = bodyLengthLine.mul(bodyRange).argMax()

      const bodyLengthStart = (await bodyLengthStartTensor.data())[0]
      const bodyLengthEnd = (await bodyLengthEndTensor.data())[0]

      bodyLengthStartTensor.dispose()
      bodyLengthEndTensor.dispose()
      bodyRange.dispose()

      console.info('Body center slice:', {
        bodyLengthIndexVal,
        bodyLengthStart,
        bodyLengthEnd,
        width: bodyLengthEnd - bodyLengthStart,
      })

      // Initial body length from the detected width
      let bodyLength = bodyLengthEnd - bodyLengthStart

      this.draw(
        canvas,
        bodyLengthStart,
        bodyLengthIndexVal,
        bodyLengthEnd,
        bodyLengthIndexVal,
        'black',
        x,
        box.topY()
      )

      // Apply rotation to potentially improve measurement
      const centerHeight = bodyLengthIndexVal
      const degrees = direction === 'left' ? -12.5 : 12.5
      const rotated = this.rotateDegrees(
        degrees,
        [centerHeight, newMiddleVal],
        croppedDetection.shape
      )

      const rotatedProjection = rotated
        .toBool()
        .logicalAnd(croppedDetection.toBool())
        .toInt()

      await this.drawImage(canvas, rotatedProjection, box, x)

      // Find front point after rotation
      const yFrontTensor = rotatedProjection
        .cumsum(1)
        .gather(tf.tensor([rotated.shape[1] - 1], [1], 'int32'), 1)
        .squeeze()
        .toBool()
        .toInt()
        .mul(tf.range(1, rotated.shape[0] + 1))
        .toInt()
        .max()
      const yFront = (await yFrontTensor.data())[0]
      yFrontTensor.dispose()

      console.info('Rotation params:', {
        centerHeight,
        newMiddleVal,
        yFront,
        bodyLengthStart,
        bodyLengthEnd,
        initialBodyLength: bodyLength,
      })

      // Store the rotated body-length line endpoints for visualization.
      // Thesis shows an angled length line (rotation method), not a horizontal one.
      // Coordinates here are in croppedDetection space and will be offset later.
      let rotatedBodyLengthLine = {
        x1: bodyLengthStart,
        y1: centerHeight,
        x2: bodyLengthEnd,
        y2: centerHeight,
      }

      // Try rotation method to refine body length measurement
      // The rotation helps account for goat's angle
      if (direction === 'right') {
        const xFrontTensor = rotatedProjection
          .cumsum(0)
          .gather(tf.tensor([rotated.shape[0] - 1], [1], 'int32'), 0)
          .squeeze()
          .toBool()
          .toInt()
          .mul(tf.range(1, rotated.shape[1] + 1))
          .toInt()
          .argMax()
        const xFront = (await xFrontTensor.data())[0]
        xFrontTensor.dispose()

        rotatedBodyLengthLine = {
          x1: xFront,
          y1: yFront,
          x2: bodyLengthStart,
          y2: centerHeight,
        }

        this.drawCirc(canvas, xFront, yFront, box, x, 'green')
        this.draw(
          canvas,
          xFront,
          yFront,
          bodyLengthStart,
          centerHeight,
          'orange',
          box.topX(),
          box.topY()
        )
        const rotatedLength = this.distance(
          xFront,
          yFront,
          bodyLengthStart,
          centerHeight
        )
        console.info('Rotation distance (right):', rotatedLength)
        // Use rotation result if it's longer (more accurate)
        if (rotatedLength > bodyLength) {
          bodyLength = rotatedLength
        }
      } else {
        const xFrontTensor = rotatedProjection
          .cumsum(0)
          .gather(tf.tensor([rotated.shape[0] - 1], [1], 'int32'), 0)
          .squeeze()
          .toBool()
          .toInt()
          .mul(tf.range(rotated.shape[1] + 1, 1, -1))
          .toInt()
          .argMax()
        const xFront = (await xFrontTensor.data())[0]
        xFrontTensor.dispose()

        rotatedBodyLengthLine = {
          x1: bodyLengthEnd,
          y1: centerHeight,
          x2: xFront,
          y2: yFront,
        }

        this.drawCirc(canvas, xFront, yFront, box, x, 'green')
        this.draw(
          canvas,
          bodyLengthEnd,
          centerHeight,
          xFront,
          yFront,
          'orange',
          box.topX(),
          box.topY()
        )
        const rotatedLength = this.distance(
          xFront,
          yFront,
          bodyLengthEnd,
          centerHeight
        )
        console.info('Rotation distance (left):', rotatedLength)
        // Use rotation result if it's longer (more accurate)
        if (rotatedLength > bodyLength) {
          bodyLength = rotatedLength
        }
      }

      // Calculate shoulder height
      const shoulderIndex = tf.scalar(
        lowestShoulderIndexVal + regions.shoulderStart,
        'int32'
      )
      const shoulderStart = croppedDetection
        .gather(shoulderIndex, 1)
        .squeeze()
        .mul(colRange.reverse())
        .argMax()
      const shoulderEnd = lastIndices.gather(shoulderIndex)

      const shoulderStartVal = (await shoulderStart.data())[0]
      const shoulderEndVal = (await shoulderEnd.data())[0]

      const shoulderIndexVal = (await shoulderIndex.data())[0]

      this.draw(
        canvas,
        shoulderIndexVal,
        shoulderStartVal,
        shoulderIndexVal,
        shoulderEndVal,
        'blue',
        x,
        box.topY()
      )

      const shoulderHeight = shoulderEndVal - shoulderStartVal

      // Calculate rump height
      const rumpIndex = tf.scalar(
        lowestRumpIndexVal + regions.rumpStart,
        'int32'
      )
      const rumpTop = croppedDetection
        .gather(hill.add(tf.scalar(regions.rumpStart, 'int32')).toInt(), 1)
        .squeeze()
        .mul(colRange.reverse())
        .argMax()
      const rumpBottom = lastIndices.gather(rumpIndex)

      const rumpTopVal = (await rumpTop.data())[0]
      const rumpBottomVal = (await rumpBottom.data())[0]
      const rumpIndexVal = (await rumpIndex.data())[0]

      this.draw(
        canvas,
        rumpIndexVal,
        rumpTopVal,
        rumpIndexVal,
        rumpBottomVal,
        'orange',
        x,
        box.topY()
      )

      const rumpHeight = rumpBottomVal - rumpTopVal

      // Cleanup tensors
      detection.dispose()
      croppedDetection.dispose()
      range.dispose()
      filledColumns.dispose()
      xStart.dispose()
      xEnd.dispose()
      colRange.dispose()
      lastIndices.dispose()
      shoulderSide.dispose()
      lowestShoulderIndex.dispose()
      rumpSide.dispose()
      lowestRumpIndex.dispose()
      hill.dispose()
      newMiddle.dispose()
      middleColumn.dispose()
      middleStart.dispose()
      middleEnd.dispose()
      middleLength.dispose()
      bodyLengthIndex.dispose()
      bodyLengthLine.dispose()
      rotated.dispose()
      rotatedProjection.dispose()
      shoulderIndex.dispose()
      shoulderStart.dispose()
      shoulderEnd.dispose()
      rumpIndex.dispose()
      rumpTop.dispose()
      rumpBottom.dispose()

      performance.mark('end_measurement')

      const result = {
        bodyLength: bodyLength,
        shoulderHeight: shoulderHeight,
        rumpHeight: rumpHeight,
        bodyHeight: bodyHeight,
        points: {
          bodyLength: {
            // Use the rotated line (thesis-style) instead of a horizontal midline.
            x1: x + rotatedBodyLengthLine.x1,
            y1: box.topY() + rotatedBodyLengthLine.y1,
            x2: x + rotatedBodyLengthLine.x2,
            y2: box.topY() + rotatedBodyLengthLine.y2,
          },
          bodyHeight: {
            x1: x + newMiddleVal,
            y1: box.topY() + middleStartVal,
            x2: x + newMiddleVal,
            y2: box.topY() + middleEndVal,
          },
          shoulderHeight: {
            x1: x + shoulderIndexVal,
            y1: box.topY() + shoulderStartVal,
            x2: x + shoulderIndexVal,
            y2: box.topY() + shoulderEndVal,
          },
          rumpHeight: {
            x1: x + rumpIndexVal,
            y1: box.topY() + rumpTopVal,
            x2: x + rumpIndexVal,
            y2: box.topY() + rumpBottomVal,
          },
        },
      }

      console.log('Measurements (pixels):', result)
      console.groupEnd()

      return result
    } catch (error) {
      console.error('Measurement extraction failed:', error)
      console.groupEnd()
      throw error
    }
  }

  /**
   * Calculate body region boundaries based on direction
   * From thesis utils.ts
   */
  calculateBodyRegions(width, direction) {
    const headWidth = Math.floor(width * this.BODY_REGIONS.head)
    const shoulderWidth = Math.floor(width * this.BODY_REGIONS.shoulder)
    const rumpWidth = Math.floor(width * this.BODY_REGIONS.rump)
    const tailWidth = Math.floor(width * this.BODY_REGIONS.tail)

    let headStart, shoulderStart, rumpStart, tailStart

    if (direction === 'left') {
      headStart = 0
      shoulderStart = headWidth
      rumpStart = headWidth + shoulderWidth
      tailStart = width - tailWidth
    } else {
      tailStart = 0
      rumpStart = tailWidth
      shoulderStart = rumpWidth + tailWidth
      headStart = rumpWidth + shoulderWidth + tailWidth
    }

    return {
      headStart,
      headWidth,
      shoulderStart,
      shoulderWidth,
      rumpStart,
      rumpWidth,
      tailStart,
      tailWidth,
    }
  }

  /**
   * Sync binary RLE (Run Length Encoding)
   * From thesis utils.ts syncBinaryRle()
   */
  syncBinaryRle(tensor) {
    const data = tensor.dataSync()
    let length = 0
    let currentStartIndex = 0
    let maxLength = 0
    let startIndex = 0
    let endIndex = 0

    for (let i = 0; i < data.length; i++) {
      if (data[i] === 1) {
        if (length === 0) {
          currentStartIndex = i
        }
        length++
        if (length > maxLength) {
          maxLength = length
          startIndex = currentStartIndex
          endIndex = i
        }
      } else {
        length = 0
      }
    }

    return [startIndex, endIndex, maxLength]
  }

  /**
   * Find first hill (peak) in tensor
   * From thesis utils.ts firstHill()
   */
  firstHill(tensor) {
    const left = tensor.slice(0, tensor.shape[0] - 1)
    const right = tensor.slice(1, tensor.shape[0] - 1)
    const leftDiff = left
      .sub(right)
      .less(0)
      .toInt()
      .mul(tf.range(tensor.shape[0], 1))
      .argMax()

    left.dispose()
    right.dispose()
    return leftDiff
  }

  /**
   * Rotate degrees
   * From thesis utils.ts rotateDegrees()
   */
  rotateDegrees(degrees, center, shape) {
    const [height, width] = center
    let line = tf.zeros(shape, 'int32')
    const buffer = line.bufferSync()

    for (let i = 0; i < shape[1]; i++) {
      buffer.set(255, height, i)
    }

    line.dispose()
    line = buffer.toTensor().toFloat().expandDims(0).expandDims(-1)

    const rotated = tf.image.rotateWithOffset(
      line,
      (Math.PI * -degrees) / 180,
      0,
      width / shape[1]
    )

    line.dispose()
    const result = rotated.squeeze().toInt()
    rotated.dispose()

    return result
  }

  /**
   * Calculate euclidean distance
   */
  distance(x1, y1, x2, y2) {
    return Math.sqrt((x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2))
  }

  /**
   * Draw image overlay on canvas for measurement debugging
   * Scales the binary mask overlay to match the current destination canvas size
   */
  async drawImage(canvas, mask, box, xOffset) {
    if (!canvas) return

    const [maskH, maskW] = mask.shape

    // Create a normalized frame for the mask (reference 640px)
    // and the high-res destination canvas (e.g. 4032px)
    const [frameH, frameW] = [
      Math.max(maskH + box.topY(), 640),
      Math.max(maskW + xOffset, 640),
    ]

    // Pad mask to the full segmentation input frame (640px base)
    const padded = mask.pad([
      [box.topY(), Math.max(0, frameH - box.topY() - maskH)],
      [xOffset, Math.max(0, frameW - xOffset - maskW)],
    ])

    const newOverlay = tf.tidy(() => {
      const expandedMask = padded.expandDims(-1)
      const overlay = tf.zeros([padded.shape[0], padded.shape[1], 4], 'int32')
      return tf.where(
        expandedMask.greater(0),
        tf.tensor1d([0, 0, 255, 180], 'int32'), // Semi-transparent blue for the measurement view
        overlay
      )
    })

    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = padded.shape[1]
    tempCanvas.height = padded.shape[0]
    await tf.browser.toPixels(newOverlay, tempCanvas)

    const ctx = canvas.getContext('2d')
    // Scale the 640x640 measurement mask back to the original photo resolution for display
    ctx.drawImage(tempCanvas, 0, 0, canvas.width, canvas.height)

    padded.dispose()
    newOverlay.dispose()
  }

  /**
   * Draw rectangle on canvas
   */
  drawRect(canvas, x, y, width, height, style) {
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    // Scale coordinates if canvas is larger than expected mask space
    const scaleX = canvas.width > 640 ? canvas.width / 640 : 1
    const scaleY = canvas.height > 640 ? canvas.height / 640 : 1

    ctx.fillStyle = style
    ctx.fillRect(x * scaleX, y * scaleY, width * scaleX, height * scaleY)
  }

  /**
   * Draw vertical line on canvas
   */
  drawVerticalLine(canvas, x, style) {
    if (!canvas) return
    const scale = Math.max(canvas.width, canvas.height) / 640
    this.draw(
      canvas,
      x * scale,
      0,
      x * scale,
      canvas.height,
      style,
      0,
      0,
      false
    )
  }

  /**
   * Draw line on canvas
   */
  draw(canvas, x1, y1, x2, y2, style, xOffset, yOffset, applyScale = true) {
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    let scale = 1
    if (applyScale) {
      scale = Math.max(canvas.width, canvas.height) / 640
    }

    ctx.beginPath()
    ctx.moveTo((x1 + xOffset) * scale, (y1 + yOffset) * scale)
    ctx.lineTo((x2 + xOffset) * scale, (y2 + yOffset) * scale)
    ctx.strokeStyle = style
    ctx.lineWidth = Math.max(2, 3 * scale)
    ctx.stroke()
  }

  /**
   * Draw small circle/marker
   */
  drawCirc(canvas, x, y, box, xOffset, style) {
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    const scale = Math.max(canvas.width, canvas.height) / 640

    ctx.fillStyle = style
    ctx.beginPath()
    ctx.arc(
      (x + xOffset) * scale,
      (y + box.topY()) * scale,
      5 * scale,
      0,
      Math.PI * 2
    )
    ctx.fill()
  }
}
